import { ProjectSearchFilters } from '../../../../clients/shared/ProjectSearch';

export const PROJECT_SEARCH_SIDEBAR_FILTER_EVENT =
  'bugherd:project-search-sidebar-filter';

export type ProjectSearchSidebarFilterDimension =
  | 'assignee'
  | 'createdBy'
  | 'involves'
  | 'tag';

export type ProjectSearchSidebarFilter = {
  dimension: ProjectSearchSidebarFilterDimension;
  token: string;
};

export type ProjectSearchSidebarUser = {
  id: number;
  role: string;
};

export const sidebarFilterForUser = (
  user: ProjectSearchSidebarUser,
  canAssignGuests = false
): ProjectSearchSidebarFilter => {
  const token = String(user.id);

  if (user.role === 'guest') {
    return canAssignGuests
      ? { dimension: 'involves', token }
      : { dimension: 'createdBy', token };
  }

  if (user.role === 'collaborator') {
    return { dimension: 'createdBy', token };
  }

  return { dimension: 'assignee', token };
};

export const sidebarFilterForTag = (tag: {
  id: number | string;
}): ProjectSearchSidebarFilter => ({
  dimension: 'tag',
  token: String(tag.id)
});

// A left-nav click narrows to a single person or tag, so it replaces any
// existing selection instead of stacking onto it. People are cleared across
// all three person dimensions because one person can map to any of them.
export const applySidebarFilterToProjectSearchFilters = (
  filters: ProjectSearchFilters,
  sidebarFilter: ProjectSearchSidebarFilter
): ProjectSearchFilters => {
  if (sidebarFilter.dimension === 'tag') {
    return { ...filters, tag: [sidebarFilter.token], tagMatch: 'any' };
  }

  const nextFilters = {
    ...filters,
    assignee: [],
    createdBy: [],
    involves: []
  };

  switch (sidebarFilter.dimension) {
    case 'assignee':
      nextFilters.assignee = [sidebarFilter.token];
      break;
    case 'createdBy':
      nextFilters.createdBy = [sidebarFilter.token];
      break;
    case 'involves':
      nextFilters.involves = [sidebarFilter.token];
      break;
    default:
      return filters;
  }

  return nextFilters;
};

export const dispatchProjectSearchSidebarFilter = (
  sidebarFilter: ProjectSearchSidebarFilter
) => {
  if (typeof window === 'undefined') return;

  window.dispatchEvent(
    new CustomEvent(PROJECT_SEARCH_SIDEBAR_FILTER_EVENT, {
      detail: sidebarFilter
    })
  );
};

export const subscribeToProjectSearchSidebarFilters = (
  onFilter: (sidebarFilter: ProjectSearchSidebarFilter) => void
) => {
  if (typeof window === 'undefined') return () => undefined;

  const handleFilter = (event: Event) => {
    const sidebarFilter = (event as CustomEvent<ProjectSearchSidebarFilter>)
      .detail;
    if (!sidebarFilter?.dimension || !sidebarFilter.token) return;
    onFilter(sidebarFilter);
  };

  window.addEventListener(PROJECT_SEARCH_SIDEBAR_FILTER_EVENT, handleFilter);
  return () =>
    window.removeEventListener(
      PROJECT_SEARCH_SIDEBAR_FILTER_EVENT,
      handleFilter
    );
};
